import { create } from 'zustand';
import Konva from 'konva';
import { CustomerDesign, DesignTemplate } from '@/types';
import { designApi, extractResource } from '@/lib/api';
import toast from 'react-hot-toast';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Konva JSON has dynamic shape
type SideJson = any;

interface DesignState {
  currentDesign: CustomerDesign | null;
  canvas: Konva.Stage | null;
  activeObject: Konva.Node | null;
  history: string[];
  historyIndex: number;
  canUndo: boolean;
  canRedo: boolean;
  isSaving: boolean;
  // Bumped on every mutation so autosave can skip when nothing's changed.
  // The save() and autosave loops compare against lastSavedDirtyCounter.
  dirtyCounter: number;
  lastSavedDirtyCounter: number;
  hasConflict: boolean;
  zoom: number;
  activeSide: number;
  hasBackSide: boolean;
  // Cached per-side canvas JSON + per-side preview image data URLs.
  // sideData[0] = front, sideData[1] = back. Populated lazily on side switches & saves.
  sideData: [SideJson | null, SideJson | null];
  sidePreviews: [string | null, string | null];
  // Uniform factor that converts the design's SOURCE coordinate space (the
  // space canvas_json / sideData are stored in — e.g. the template's 1051x602)
  // into the current STAGE display space (e.g. 600x344). Set by useKonvaCanvas
  // at load. Renders multiply by it; serialization divides by it, so coords
  // always round-trip in source space. 1 = no scaling (already stage-space).
  sourceScale: number;
  setCanvas: (stage: Konva.Stage) => void;
  setActiveObject: (node: Konva.Node | null) => void;
  setCurrentDesign: (design: CustomerDesign) => void;
  setHasBackSide: (has: boolean) => void;
  saveState: () => void;
  save: () => Promise<void>;
  markDirty: () => void;
  setHasConflict: (v: boolean) => void;
  reloadFromServer: () => Promise<void>;
  undo: () => void;
  redo: () => void;
  loadTemplate: (template: DesignTemplate) => void;
  exportForPrint: () => Promise<string>;
  setZoom: (zoom: number) => void;
  setActiveSide: (side: number) => void;
  addText: (text: string, options?: { fontSize?: number; fontWeight?: string; fontFamily?: string; fill?: string }) => void;
  addImage: (url: string) => void;
  addShape: (shape: string) => void;
  addLine: () => void;
  /**
   * Set the canvas background fill — used by the apparel "Material color" panel
   * to tint the print area so it simulates the garment colour (works even when
   * the product has no mockup photo). Mutates the background Rect's fill and
   * records an undo snapshot.
   */
  setBgColor: (color: string, commit?: boolean) => void;
  deleteActiveObject: () => void;
  duplicateActiveObject: () => void;
  bringToFront: () => void;
  sendToBack: () => void;
  toggleLock: (node: Konva.Node) => void;
  toggleVisibility: (node: Konva.Node) => void;
  setImageFilter: (node: Konva.Node, filters: { brightness?: number; contrast?: number; grayscale?: boolean }) => void;
  nudgeActiveObject: (dx: number, dy: number) => void;
  /**
   * Replace the active side's content with a generated canvas_json blob.
   * Used by the AI design generator: clears user-authored nodes (preserves
   * background + transformer + print guides) and loads the supplied objects.
   * Records an undo snapshot afterwards so the customer can revert.
   */
  applyCanvasJson: (json: SideJson) => void;
}

/** Get the main editable layer (first layer, or create one) */
function getLayer(stage: Konva.Stage): Konva.Layer {
  const layers = stage.getLayers();
  if (layers.length > 0) return layers[0];
  const layer = new Konva.Layer();
  stage.add(layer);
  return layer;
}

/** Get the transformer in the layer */
function getTransformer(layer: Konva.Layer): Konva.Transformer | null {
  return (layer.findOne('Transformer') as Konva.Transformer) || null;
}

/**
 * Run `fn` with the print-guides layer hidden. Used for thumbnail/preview generation
 * so the bleed/safe-zone overlay doesn't appear in exported images.
 */
export function withPrintGuidesHidden<T>(stage: Konva.Stage, fn: () => T): T {
  const guides = stage
    .getLayers()
    .find((l) => l.getAttr('_esyMeta')?.role === 'print-guides');
  if (!guides) return fn();
  const wasVisible = guides.visible();
  guides.visible(false);
  guides.draw();
  try {
    return fn();
  } finally {
    guides.visible(wasVisible);
    guides.draw();
  }
}

/** Serialize the editable layer (everything except transformer + background) to a Konva JSON blob. */
function serializeLayer(stage: Konva.Stage): SideJson {
  const layer = getLayer(stage);
  const nodes = (layer.getChildren() as Konva.Node[]).filter((n) => {
    if (n instanceof Konva.Transformer) return false;
    // Exclude ephemeral overlays (smart guides) — they should never be persisted.
    if (n.getAttr('_esyMeta')?.role === 'smart-guide') return false;
    return true;
  });
  const bgNode = nodes.find((n) => n.getAttr('_esyMeta')?.role === 'background');
  const userNodes = nodes.filter((n) => n !== bgNode);

  return {
    bgColor: bgNode ? (bgNode as Konva.Rect).fill() : '#ffffff',
    objects: userNodes.map((n) => {
      const attrs: Record<string, unknown> = { ...n.attrs };
      // Strip non-serializable live Image references — image data URL is kept in _imgSrc.
      if (attrs.image) delete attrs.image;
      return { className: n.className, attrs };
    }),
  };
}

/** Remove all user-editable nodes from the main layer (keep background + transformer). */
function clearLayer(stage: Konva.Stage) {
  const layer = getLayer(stage);
  const tr = getTransformer(layer);
  if (tr) tr.nodes([]);
  // Copy children list first — destroy() mutates the live array returned by getChildren().
  [...(layer.getChildren() as Konva.Node[])].forEach((child) => {
    if (child instanceof Konva.Transformer) return;
    const meta = child.getAttr('_esyMeta');
    if (meta?.role === 'background') return;
    child.destroy();
  });
  layer.batchDraw();
}

// Attribute keys that are absolute lengths/positions and therefore scale with
// the source<->stage factor. scaleX/scaleY are deliberately excluded — they are
// multipliers already baked into the node and must pass through untouched.
const SCALABLE_NUM_KEYS = [
  'x', 'y', 'width', 'height', 'fontSize', 'strokeWidth',
  'radius', 'radiusX', 'radiusY', 'offsetX', 'offsetY', 'cornerRadius',
];

function scaleAttrsBy(attrs: Record<string, unknown>, factor: number): Record<string, unknown> {
  if (!attrs || factor === 1) return attrs;
  const a: Record<string, unknown> = { ...attrs };
  for (const k of SCALABLE_NUM_KEYS) {
    if (typeof a[k] === 'number') a[k] = (a[k] as number) * factor;
  }
  if (Array.isArray(a.points)) {
    a.points = (a.points as unknown[]).map((n) => (typeof n === 'number' ? n * factor : n));
  }
  return a;
}

// Scale every object in a side payload by `factor`. Converts a side between
// source space (how sideData/canvas_json are stored) and stage space (how it
// renders): multiply by sourceScale to render, divide to serialize.
function scaleSideObjects(side: SideJson, factor: number): SideJson {
  if (!side || typeof side !== 'object' || factor === 1) return side;
  const objects = Array.isArray(side.objects) ? side.objects : [];
  return {
    ...side,
    objects: objects.map((o: { attrs?: Record<string, unknown> }) =>
      o && o.attrs ? { ...o, attrs: scaleAttrsBy(o.attrs, factor) } : o,
    ),
  };
}

/** Apply a serialized side back to the canvas (bg color + objects). */
function applySide(stage: Konva.Stage, data: SideJson | null) {
  if (!data) return;
  const layer = getLayer(stage);
  const tr = getTransformer(layer);

  if (data.bgColor) {
    const bgNode = (layer.getChildren() as Konva.Node[]).find(
      (n) => n.getAttr('_esyMeta')?.role === 'background',
    ) as Konva.Rect | undefined;
    if (bgNode) {
      // If the stage started transparent (because a garment mockup sits behind
      // the canvas), don't let a saved bgColor of white stomp it — doing so
      // would paint a white sheet over the shirt and hide the realistic
      // backdrop. A genuinely coloured bg is still honoured.
      const currentFill = (bgNode.fill() as string) || '';
      const isStageTransparent = currentFill === 'rgba(0,0,0,0)' || currentFill === 'transparent' || currentFill === '';
      const isIncomingDefaultWhite = /^(#fff(fff)?|white|rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(,\s*1(\.0+)?)?\s*\))$/i.test(
        String(data.bgColor).trim(),
      );
      if (!(isStageTransparent && isIncomingDefaultWhite)) {
        bgNode.fill(data.bgColor);
      }
    }
  }

  // sideData is stored in SOURCE space; scale every object into the current
  // STAGE space so a side-switch / reload matches the initially-rendered front.
  const factor = useDesignStore.getState().sourceScale || 1;
  const objects = (data.objects || []) as Array<{
    className: string;
    attrs: Record<string, unknown>;
  }>;
  objects.forEach((obj) => {
    try {
      const attrs = scaleAttrsBy(obj.attrs || {}, factor);

      if (obj.className === 'Image' && attrs._imgSrc) {
        const imgSrc = attrs._imgSrc as string;
        const imageObj = new window.Image();
        imageObj.crossOrigin = 'anonymous';
        imageObj.onload = () => {
          const node = new Konva.Image({
            ...(attrs as Konva.ImageConfig),
            image: imageObj,
          });
          node.setAttr('_imgSrc', imgSrc);
          layer.add(node as unknown as Konva.Group);
          if (tr) tr.moveToTop();
          layer.batchDraw();
        };
        imageObj.src = imgSrc;
        return;
      }

      let node: Konva.Node | null = null;
      if (obj.className === 'Text') node = new Konva.Text(attrs as Konva.TextConfig);
      else if (obj.className === 'Rect') node = new Konva.Rect(attrs as Konva.RectConfig);
      else if (obj.className === 'Circle') node = new Konva.Circle(attrs as Konva.CircleConfig);
      else if (obj.className === 'Line') node = new Konva.Line(attrs as Konva.LineConfig);
      else if (obj.className === 'RegularPolygon')
        node = new Konva.RegularPolygon(attrs as Konva.RegularPolygonConfig);
      else if (obj.className) {
        // Any other Konva shape (Star, Ring, Arrow, Ellipse, Wedge, Arc, Path…).
        // Konva.Node.create reconstructs from className so saved shapes survive.
        try { node = Konva.Node.create(JSON.stringify({ className: obj.className, attrs })); } catch { node = null; }
      }
      if (node) layer.add(node as unknown as Konva.Group);
    } catch {
      /* skip invalid node */
    }
  });

  if (tr) tr.moveToTop();
  layer.batchDraw();

  // Web fonts used by this side may not be downloaded yet — Konva would render
  // a serif fallback (e.g. a back-side "Bungee Inline" heading). Explicitly load
  // each font this side uses, then re-measure and redraw. An @font-face with
  // display=swap won't fetch from a canvas reference alone, so the explicit
  // load() is required.
  if (typeof document !== 'undefined' && document.fonts?.load) {
    const families = new Set<string>();
    objects.forEach((obj) => {
      const fam = obj?.attrs?.fontFamily as string | undefined;
      if (obj?.className === 'Text' && fam) families.add(fam);
    });
    if (families.size > 0) {
      Promise.all(
        Array.from(families).map((f) => document.fonts.load(`16px "${f}"`).catch(() => undefined)),
      ).then(() => {
        (layer.getChildren() as Konva.Node[]).forEach((n) => {
          if (n instanceof Konva.Text) n.fontFamily(n.fontFamily());
        });
        layer.batchDraw();
      });
    }
  }
}

export const useDesignStore = create<DesignState>((set, get) => ({
  currentDesign: null,
  canvas: null,
  activeObject: null,
  history: [],
  historyIndex: -1,
  canUndo: false,
  canRedo: false,
  isSaving: false,
  dirtyCounter: 0,
  lastSavedDirtyCounter: 0,
  hasConflict: false,
  zoom: 1,
  activeSide: 0,
  hasBackSide: false,
  sideData: [null, null],
  sidePreviews: [null, null],
  sourceScale: 1,

  setCanvas: (stage) => {
    set({ canvas: stage });
    // Save initial state
    const json = stage.toJSON();
    set({ history: [json], historyIndex: 0 });
  },

  setActiveObject: (node) => {
    set({ activeObject: node });
  },

  setCurrentDesign: (design) => {
    set({ currentDesign: design });
  },

  setHasBackSide: (has) => {
    set({ hasBackSide: has });
  },

  saveState: () => {
    const { canvas, history, historyIndex, dirtyCounter } = get();
    if (!canvas) return;
    const json = canvas.toJSON();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(json);
    // Limit history to 50 items
    if (newHistory.length > 50) newHistory.shift();
    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
      canUndo: newHistory.length > 1,
      canRedo: false,
      dirtyCounter: dirtyCounter + 1,
    });
  },

  markDirty: () => set((s) => ({ dirtyCounter: s.dirtyCounter + 1 })),

  setHasConflict: (v) => set({ hasConflict: v }),

  reloadFromServer: async () => {
    const { currentDesign, canvas } = get();
    if (!currentDesign) return;
    try {
      const res = await designApi.getDesign(currentDesign.id);
      const fresh = extractResource<CustomerDesign>(res.data, 'design');
      set({
        currentDesign: fresh,
        hasConflict: false,
        dirtyCounter: 0,
        lastSavedDirtyCounter: 0,
      });
      // Replace canvas content with the server payload. Re-uses applySide so
      // both single-side and two-side designs land in the right structure.
      if (canvas) {
        const stage = canvas as Konva.Stage;
        const json = fresh.canvas_json;
        clearLayer(stage);
        if (json && (json.front || json.back)) {
          applySide(stage, json.front ?? null);
        } else {
          applySide(stage, json);
        }
      }
    } catch {
      toast.error('Failed to reload latest version.');
    }
  },

  save: async () => {
    const { canvas, currentDesign, hasBackSide, activeSide, sideData, sidePreviews } = get();
    if (!canvas || !currentDesign) return;

    set({ isSaving: true });
    try {
      const stage = canvas as Konva.Stage;
      const tr = getTransformer(getLayer(stage));
      if (tr) tr.nodes([]);
      stage.batchDraw();

      // Snapshot the side currently on screen so the cache is fresh. The stage
      // is in display space; divide by sourceScale so the cached side (and the
      // persisted canvas_json) stays in source space and round-trips on reload.
      const factor = get().sourceScale || 1;
      const currentJson = scaleSideObjects(serializeLayer(stage), factor > 0 ? 1 / factor : 1);
      const currentPreview = withPrintGuidesHidden(stage, () =>
        stage.toDataURL({
          pixelRatio: 0.5,
          mimeType: 'image/png',
          quality: 0.8,
        }),
      );

      const freshSideData: [SideJson | null, SideJson | null] = [...sideData] as [SideJson | null, SideJson | null];
      const freshSidePreviews: [string | null, string | null] = [...sidePreviews] as [string | null, string | null];
      freshSideData[activeSide] = currentJson;
      freshSidePreviews[activeSide] = currentPreview;

      let canvasJsonPayload: SideJson;
      let previewImage: string;
      let previewImageBack: string | null = null;

      if (hasBackSide) {
        canvasJsonPayload = {
          front: freshSideData[0] ?? currentJson,
          back: freshSideData[1] ?? { bgColor: '#ffffff', objects: [] },
        };
        previewImage = freshSidePreviews[0] ?? currentPreview;
        previewImageBack = freshSidePreviews[1] ?? null;
      } else {
        canvasJsonPayload = freshSideData[0] ?? currentJson;
        previewImage = freshSidePreviews[0] ?? currentPreview;
      }

      const response = await designApi.updateDesign(currentDesign.id, {
        canvas_json: canvasJsonPayload,
        preview_image: previewImage,
        ...(hasBackSide ? { preview_image_back: previewImageBack } : {}),
        expected_version_id: currentDesign.current_version_id ?? null,
      });

      const dirtyAtSave = get().dirtyCounter;
      set({
        currentDesign: extractResource<CustomerDesign>(response.data, 'design'),
        sideData: freshSideData,
        sidePreviews: freshSidePreviews,
        isSaving: false,
        lastSavedDirtyCounter: dirtyAtSave,
        hasConflict: false,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      set({ isSaving: false });
      const data = err?.response?.data;
      const status = err?.response?.status;
      // 409 from the conflict-detection branch — flag the editor so the banner
      // can offer a Reload button. We do NOT toast here; the banner is calmer.
      if (status === 409 && data?.code === 'version_conflict') {
        set({ hasConflict: true });
        return;
      }
      // Surface the real reason — usually a validation error (e.g. canvas_json too large,
      // preview_image string too long) or 401 auth expired. Dump it to the console so the
      // network tab can cross-reference what the backend said.
      console.error('Save design failed:', { status, data, err });
      const message =
        data?.message ||
        (data?.errors && Object.values(data.errors).flat()[0]) ||
        (status === 413 ? 'Design is too large to save — try flattening layers or reducing image sizes.' : null) ||
        (status === 401 ? 'Session expired. Please log in again.' : null) ||
        'Failed to save design. Check your connection.';
      toast.error(String(message));
    }
  },

  undo: () => {
    const { canvas, history, historyIndex } = get();
    if (!canvas || historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    const parsed = JSON.parse(history[newIndex]);

    // Destroy current content and reload
    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    // Remove all children except transformer. Copy the array first — getChildren()
    // returns the live list, and destroy() mutates it, causing forEach to skip nodes.
    [...(layer.getChildren() as Konva.Node[])].forEach((child) => {
      if (!(child instanceof Konva.Transformer)) {
        child.destroy();
      }
    });

    // Recreate nodes from saved JSON
    if (parsed.children?.[0]?.children) {
      parsed.children[0].children.forEach((childData: { className: string; attrs: Record<string, unknown> }) => {
        if (childData.className === 'Transformer') return;
        const node = Konva.Node.create(JSON.stringify(childData));
        if (node) layer.add(node);
      });
    }

    if (tr) {
      tr.nodes([]);
      tr.moveToTop();
    }
    layer.batchDraw();

    set({
      historyIndex: newIndex,
      canUndo: newIndex > 0,
      canRedo: true,
      activeObject: null,
    });
  },

  redo: () => {
    const { canvas, history, historyIndex } = get();
    if (!canvas || historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    const parsed = JSON.parse(history[newIndex]);

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    // Copy children list before destroying — see note in undo().
    [...(layer.getChildren() as Konva.Node[])].forEach((child) => {
      if (!(child instanceof Konva.Transformer)) {
        child.destroy();
      }
    });

    if (parsed.children?.[0]?.children) {
      parsed.children[0].children.forEach((childData: { className: string; attrs: Record<string, unknown> }) => {
        if (childData.className === 'Transformer') return;
        const node = Konva.Node.create(JSON.stringify(childData));
        if (node) layer.add(node);
      });
    }

    if (tr) {
      tr.nodes([]);
      tr.moveToTop();
    }
    layer.batchDraw();

    set({
      historyIndex: newIndex,
      canUndo: true,
      canRedo: newIndex < history.length - 1,
      activeObject: null,
    });
  },

  loadTemplate: (template) => {
    const { canvas } = get();
    if (!canvas || !template.canvas_json) return;

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    // Clear existing nodes except transformer (copy to avoid live-array mutation during destroy)
    [...(layer.getChildren() as Konva.Node[])].forEach((child) => {
      if (!(child instanceof Konva.Transformer)) {
        child.destroy();
      }
    });

    // Parse template JSON
    const parsed = typeof template.canvas_json === 'string'
      ? JSON.parse(template.canvas_json)
      : template.canvas_json;

    if (parsed.children?.[0]?.children) {
      parsed.children[0].children.forEach((childData: { className: string; attrs: Record<string, unknown> }) => {
        if (childData.className === 'Transformer') return;
        const node = Konva.Node.create(JSON.stringify(childData));
        if (node) layer.add(node);
      });
    }

    if (tr) {
      tr.nodes([]);
      tr.moveToTop();
    }
    layer.batchDraw();
    get().saveState();
  },

  exportForPrint: async () => {
    const { canvas } = get();
    if (!canvas) return '';
    return withPrintGuidesHidden(canvas as Konva.Stage, () =>
      (canvas as Konva.Stage).toDataURL({
        pixelRatio: 3, // 3x resolution for print
        mimeType: 'image/png',
        quality: 1,
      }),
    );
  },

  setZoom: (zoom) => {
    // Zoom is applied by the editor shell via a CSS `transform: scale(${zoom})`
    // wrapper around <DesignCanvas>. We intentionally do NOT call stage.scale()
    // here — doing both multiplied the effective zoom (zoom × zoom), which made
    // the transformer anchors and borders balloon (e.g. 10px anchors rendering
    // at ~90px when zoom=3). Keep stage coordinates as-is so snapping, DPI math,
    // and safe-zone checks all operate in unscaled pixels.
    const { canvas } = get();
    set({ zoom });
    if (canvas) {
      // If a prior build left a scale on the stage, reset it so the new CSS-only
      // zoom path takes effect cleanly.
      if (canvas.scaleX() !== 1 || canvas.scaleY() !== 1) {
        canvas.scale({ x: 1, y: 1 });
        canvas.batchDraw();
      }
    }
  },

  setActiveSide: (side) => {
    const { canvas, activeSide, hasBackSide, sideData, sidePreviews } = get();
    if (side === activeSide) return;
    if (side !== 0 && side !== 1) return;
    if (side === 1 && !hasBackSide) return;
    if (!canvas) {
      set({ activeSide: side });
      return;
    }

    const stage = canvas as Konva.Stage;
    const tr = getTransformer(getLayer(stage));
    if (tr) tr.nodes([]);

    // Snapshot current side's canvas + preview into the cache. Stage is display
    // space; divide by sourceScale so the cached side stays in source space.
    const factor = get().sourceScale || 1;
    const currentJson = scaleSideObjects(serializeLayer(stage), factor > 0 ? 1 / factor : 1);
    let currentPreview: string | null = null;
    try {
      currentPreview = withPrintGuidesHidden(stage, () =>
        stage.toDataURL({ pixelRatio: 0.5, mimeType: 'image/png', quality: 0.8 }),
      );
    } catch {
      /* ignore */
    }
    const newSideData: [SideJson | null, SideJson | null] = [...sideData] as [SideJson | null, SideJson | null];
    const newSidePreviews: [string | null, string | null] = [...sidePreviews] as [string | null, string | null];
    newSideData[activeSide] = currentJson;
    if (currentPreview) newSidePreviews[activeSide] = currentPreview;

    // Clear canvas + load the target side.
    clearLayer(stage);
    applySide(stage, newSideData[side]);
    set({
      activeSide: side,
      sideData: newSideData,
      sidePreviews: newSidePreviews,
      activeObject: null,
    });
  },

  addText: (text, options = {}) => {
    const { canvas } = get();
    if (!canvas) return;

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    const textNode = new Konva.Text({
      x: canvas.width() / 2 - 100,
      y: canvas.height() / 2 - 15,
      text,
      fontSize: options.fontSize || 24,
      fontFamily: options.fontFamily || 'Inter',
      fontStyle: options.fontWeight === 'bold' || options.fontWeight === '600' ? 'bold' : 'normal',
      fill: options.fill || '#333333',
      draggable: true,
      width: 200,
      letterSpacing: 0,
      lineHeight: 1.2,
    });

    layer.add(textNode);
    if (tr) {
      tr.nodes([textNode]);
      tr.moveToTop();
    }
    set({ activeObject: textNode });
    layer.batchDraw();
    get().saveState();
  },

  addImage: (url) => {
    const { canvas } = get();
    if (!canvas) return;

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    const imageObj = new window.Image();
    imageObj.crossOrigin = 'anonymous';
    imageObj.onload = () => {
      const maxSize = Math.min(canvas.width(), canvas.height()) * 0.5;
      const scale = Math.min(maxSize / imageObj.width, maxSize / imageObj.height);

      const konvaImage = new Konva.Image({
        x: canvas.width() / 2 - (imageObj.width * scale) / 2,
        y: canvas.height() / 2 - (imageObj.height * scale) / 2,
        image: imageObj,
        width: imageObj.width * scale,
        height: imageObj.height * scale,
        draggable: true,
      });
      // Preserve the source so the image can be rehydrated after save/reload — the
      // live HTMLImageElement isn't serializable, so we stash the data URL/remote URL here.
      konvaImage.setAttr('_imgSrc', url);

      layer.add(konvaImage);
      if (tr) {
        tr.nodes([konvaImage]);
        tr.moveToTop();
      }
      set({ activeObject: konvaImage });
      layer.batchDraw();
      get().saveState();
    };
    imageObj.onerror = () => {
      toast.error('Failed to load image. Please check the URL.');
    };
    imageObj.src = url;
  },

  addShape: (shape) => {
    const { canvas } = get();
    if (!canvas) return;

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);
    const centerX = canvas.width() / 2;
    const centerY = canvas.height() / 2;

    let node: Konva.Shape;

    const d = { draggable: true };
    switch (shape) {
      case 'rect':
        node = new Konva.Rect({ x: centerX - 60, y: centerY - 40, width: 120, height: 80, fill: '#FF6B00', ...d });
        break;
      case 'square':
        node = new Konva.Rect({ x: centerX - 50, y: centerY - 50, width: 100, height: 100, fill: '#FF6B00', ...d });
        break;
      case 'rounded':
        node = new Konva.Rect({ x: centerX - 60, y: centerY - 40, width: 120, height: 80, cornerRadius: 18, fill: '#FF6B00', ...d });
        break;
      case 'circle':
        node = new Konva.Circle({ x: centerX, y: centerY, radius: 55, fill: '#1B365D', ...d });
        break;
      case 'ellipse':
        node = new Konva.Ellipse({ x: centerX, y: centerY, radiusX: 70, radiusY: 45, fill: '#1B365D', ...d });
        break;
      case 'triangle':
        node = new Konva.RegularPolygon({ x: centerX, y: centerY, sides: 3, radius: 60, fill: '#FF6B00', ...d });
        break;
      case 'diamond':
        node = new Konva.RegularPolygon({ x: centerX, y: centerY, sides: 4, radius: 62, fill: '#FF6B00', ...d });
        break;
      case 'pentagon':
        node = new Konva.RegularPolygon({ x: centerX, y: centerY, sides: 5, radius: 60, fill: '#16A34A', ...d });
        break;
      case 'hexagon':
        node = new Konva.RegularPolygon({ x: centerX, y: centerY, sides: 6, radius: 60, fill: '#16A34A', ...d });
        break;
      case 'octagon':
        node = new Konva.RegularPolygon({ x: centerX, y: centerY, sides: 8, radius: 60, fill: '#16A34A', ...d });
        break;
      case 'star':
        node = new Konva.Star({ x: centerX, y: centerY, numPoints: 5, innerRadius: 28, outerRadius: 62, fill: '#FACC15', ...d });
        break;
      case 'star6':
        node = new Konva.Star({ x: centerX, y: centerY, numPoints: 6, innerRadius: 34, outerRadius: 62, fill: '#FACC15', ...d });
        break;
      case 'ring':
        node = new Konva.Ring({ x: centerX, y: centerY, innerRadius: 30, outerRadius: 60, fill: '#7C3AED', ...d });
        break;
      case 'wedge':
        node = new Konva.Wedge({ x: centerX, y: centerY, radius: 62, angle: 270, fill: '#E11D48', rotation: -90, ...d });
        break;
      case 'arc':
        node = new Konva.Arc({ x: centerX, y: centerY, innerRadius: 32, outerRadius: 62, angle: 270, fill: '#E11D48', rotation: -90, ...d });
        break;
      case 'arrow':
        node = new Konva.Arrow({ x: centerX - 70, y: centerY, points: [0, 0, 140, 0], pointerLength: 22, pointerWidth: 26, fill: '#0071BC', stroke: '#0071BC', strokeWidth: 16, ...d });
        break;
      case 'line':
        node = new Konva.Line({ points: [centerX - 80, centerY, centerX + 80, centerY], stroke: '#1B365D', strokeWidth: 6, lineCap: 'round', ...d });
        break;
      case 'heart':
        node = new Konva.Path({ x: centerX - 40, y: centerY - 36, data: 'M40,74 C40,74 6,49 6,26 C6,13 15,6 25,6 C33,6 38,11 40,17 C42,11 47,6 55,6 C65,6 74,13 74,26 C74,49 40,74 40,74 Z', fill: '#E11D48', ...d });
        break;
      case 'plus':
        node = new Konva.Path({ x: centerX - 40, y: centerY - 40, data: 'M28,0 L52,0 L52,28 L80,28 L80,52 L52,52 L52,80 L28,80 L28,52 L0,52 L0,28 L28,28 Z', fill: '#0071BC', ...d });
        break;
      default:
        return;
    }

    layer.add(node);
    if (tr) {
      tr.nodes([node]);
      tr.moveToTop();
    }
    set({ activeObject: node });
    layer.batchDraw();
    get().saveState();
  },

  setBgColor: (color, commit = true) => {
    const { canvas } = get();
    if (!canvas) return;
    const layer = getLayer(canvas);
    // Paint EVERY background-role fill shape, not just the first: template
    // designs stack the template's own background rect ON TOP of the editor's
    // base rect — recolouring only the bottom one changes nothing visible.
    const bgNodes = (layer.getChildren() as Konva.Node[]).filter(
      (n) => n.getAttr('_esyMeta')?.role === 'background'
        && !(n instanceof Konva.Image)
        && typeof (n as Konva.Shape).fill === 'function',
    );
    if (bgNodes.length === 0) return;
    bgNodes.forEach((n) => {
      const bg = n as Konva.Rect;
      bg.fill(color);
      // Clear any gradient stops so a solid fill actually shows.
      if (typeof bg.fillPriority === 'function') bg.fillPriority('color');
      if (typeof bg.fillLinearGradientColorStops === 'function') bg.fillLinearGradientColorStops([]);
    });
    layer.batchDraw();
    // commit=false lets the Background picker preview live while dragging without
    // spamming the undo history; the panel commits one snapshot on release.
    if (commit) get().saveState();
  },

  addLine: () => {
    const { canvas } = get();
    if (!canvas) return;

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    const line = new Konva.Line({
      points: [50, canvas.height() / 2, canvas.width() - 50, canvas.height() / 2],
      stroke: '#1B365D',
      strokeWidth: 2,
      draggable: true,
    });

    layer.add(line);
    if (tr) {
      tr.nodes([line]);
      tr.moveToTop();
    }
    set({ activeObject: line });
    layer.batchDraw();
    get().saveState();
  },

  deleteActiveObject: () => {
    const { canvas, activeObject } = get();
    if (!canvas || !activeObject) return;

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    if (tr) tr.nodes([]);
    activeObject.destroy();
    set({ activeObject: null });
    layer.batchDraw();
    get().saveState();
  },

  /**
   * Clone the active node and drop the copy 20px down-right so it's visually distinct.
   * Images need special handling because the `image` property is a live HTMLImageElement
   * that Konva's clone() doesn't deep-copy.
   */
  duplicateActiveObject: () => {
    const { canvas, activeObject } = get();
    if (!canvas || !activeObject) return;

    const layer = getLayer(canvas);
    const tr = getTransformer(layer);

    let clone: Konva.Node | null = null;
    if (activeObject instanceof Konva.Image) {
      const attrs = { ...activeObject.attrs } as Record<string, unknown>;
      delete attrs.image;
      const img = activeObject.image() as HTMLImageElement | undefined;
      const newImg = new window.Image();
      newImg.crossOrigin = 'anonymous';
      newImg.onload = () => {
        const node = new Konva.Image({
          ...(attrs as Konva.ImageConfig),
          x: ((attrs.x as number) ?? 0) + 20,
          y: ((attrs.y as number) ?? 0) + 20,
          image: newImg,
        });
        layer.add(node as unknown as Konva.Group);
        if (tr) {
          tr.nodes([node]);
          tr.moveToTop();
        }
        set({ activeObject: node });
        layer.batchDraw();
        get().saveState();
      };
      newImg.src = (attrs._imgSrc as string) || img?.src || '';
      return;
    }

    clone = activeObject.clone({
      x: (activeObject.x() ?? 0) + 20,
      y: (activeObject.y() ?? 0) + 20,
    });
    if (!clone) return;
    layer.add(clone as unknown as Konva.Group);
    if (tr) {
      tr.nodes([clone]);
      tr.moveToTop();
    }
    set({ activeObject: clone });
    layer.batchDraw();
    get().saveState();
  },

  bringToFront: () => {
    const { canvas, activeObject } = get();
    if (!canvas || !activeObject) return;
    activeObject.moveToTop();
    // Keep transformer above whatever the node is
    const tr = getTransformer(getLayer(canvas));
    if (tr) tr.moveToTop();
    activeObject.getLayer()?.batchDraw();
    get().saveState();
  },

  sendToBack: () => {
    const { canvas, activeObject } = get();
    if (!canvas || !activeObject) return;
    // Don't go below the background rect. moveToBottom() would put us underneath it.
    // Instead, use zIndex = 1 (index 0 reserved for background).
    const layer = getLayer(canvas);
    const bg = (layer.getChildren() as Konva.Node[]).find(
      (n) => n.getAttr('_esyMeta')?.role === 'background',
    );
    if (bg) {
      activeObject.zIndex(bg.zIndex() + 1);
    } else {
      activeObject.moveToBottom();
    }
    const tr = getTransformer(layer);
    if (tr) tr.moveToTop();
    layer.batchDraw();
    get().saveState();
  },

  /**
   * Toggle a node's locked state. Stored in `_esyLocked` attribute so it persists
   * across save/load. Locked nodes can't be dragged or selected via the transformer.
   */
  toggleLock: (node) => {
    const { canvas, activeObject } = get();
    if (!canvas) return;
    const locked = !node.getAttr('_esyLocked');
    node.setAttr('_esyLocked', locked);
    node.draggable(!locked);
    // If currently selected and now locked, clear selection.
    if (locked && activeObject === node) {
      const tr = getTransformer(getLayer(canvas));
      if (tr) tr.nodes([]);
      set({ activeObject: null });
    }
    canvas.batchDraw();
  },

  /**
   * Toggle a node's visibility. Hidden nodes are preserved in JSON but not rendered.
   * We use Konva's built-in visible() which also removes it from pointer hit-testing.
   */
  toggleVisibility: (node) => {
    const { canvas, activeObject } = get();
    if (!canvas) return;
    const visible = !node.visible();
    node.visible(visible);
    if (!visible && activeObject === node) {
      const tr = getTransformer(getLayer(canvas));
      if (tr) tr.nodes([]);
      set({ activeObject: null });
    }
    canvas.batchDraw();
  },

  /**
   * Apply a combined set of filters to an image node. brightness is -1..1,
   * contrast is -100..100, grayscale is a boolean. Konva requires cache() for
   * filters to take effect. The filter values are stored in `_esyFilters`
   * so they persist and the UI can reflect the current state.
   */
  setImageFilter: (node, filters) => {
    const { canvas } = get();
    if (!canvas) return;
    if (!(node instanceof Konva.Image)) return;

    // Merge with previously-applied filter state.
    const prev = (node.getAttr('_esyFilters') as
      | { brightness?: number; contrast?: number; grayscale?: boolean }
      | undefined) || {};
    const next = { ...prev, ...filters };
    node.setAttr('_esyFilters', next);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Konva filter fns
    const active: any[] = [];
    if (typeof next.brightness === 'number' && next.brightness !== 0) {
      active.push(Konva.Filters.Brighten);
      node.brightness(next.brightness);
    }
    if (typeof next.contrast === 'number' && next.contrast !== 0) {
      active.push(Konva.Filters.Contrast);
      node.contrast(next.contrast);
    }
    if (next.grayscale) {
      active.push(Konva.Filters.Grayscale);
    }

    if (active.length === 0) {
      node.filters([]);
      node.clearCache();
    } else {
      node.filters(active);
      node.cache();
    }
    canvas.batchDraw();
  },

  nudgeActiveObject: (dx, dy) => {
    const { canvas, activeObject } = get();
    if (!canvas || !activeObject) return;
    activeObject.x(activeObject.x() + dx);
    activeObject.y(activeObject.y() + dy);
    const tr = getTransformer(getLayer(canvas));
    if (tr) tr.forceUpdate();
    canvas.batchDraw();
    get().saveState();
  },

  applyCanvasJson: (json) => {
    const { canvas } = get();
    if (!canvas) return;
    const stage = canvas as Konva.Stage;
    // Clear user-authored nodes (background survives, transformer survives).
    clearLayer(stage);
    // Apply the new objects + bg color. `applySide` is a no-op if json is null.
    applySide(stage, json);
    set({ activeObject: null });
    get().saveState();
  },
}));
